import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  Database_Procs_GetRecentActivityByTeam_Item,
  Database_Procs_GetTeamHistory_Item,
  Database_Procs_TeamTopPlayers_Item,
  Entities_FluencyGamesResult_TeamResult,
  Entities_Team_GetTeamPlayerOfTheDay_PlayerOftheDay,
  LoginId,
  TeamId
} from '@suntex.fim/api-client'
import {
  $SpecialGradeLevel,
  EntityLevel,
  MetaKey,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'

// --- Query Keys ---

export const dashboardKeys = {
  all: ['dashboard'] as const,
  teamIndex: (teamId: TeamId, loginId: LoginId, day: number) =>
    [...dashboardKeys.all, 'teamIndex', teamId, loginId, day] as const,
  dashboardConfig: (loginId: LoginId) =>
    [...dashboardKeys.all, 'dashboardConfig', loginId] as const,
  recentActivity: (teamId: TeamId) =>
    [...dashboardKeys.all, 'recentActivity', teamId] as const,
  fluencyGames: (teamId: TeamId, year: string, grade: number) =>
    [...dashboardKeys.all, 'fluencyGames', teamId, year, grade] as const,
  teamHistory: (teamId: TeamId, mode: number) =>
    [...dashboardKeys.all, 'teamHistory', teamId, mode] as const
}

// --- Types ---

interface TeamIndexData {
  topPlayers: Database_Procs_TeamTopPlayers_Item[]
  playersOfDay: Entities_Team_GetTeamPlayerOfTheDay_PlayerOftheDay[]
}

interface DashboardConfigData {
  [key: string]: boolean
}

// --- Queries ---

export function useTeamIndexQuery(
  teamId: TeamId | undefined,
  loginId: LoginId | undefined,
  day: number,
  options?: { enabled?: boolean }
) {
  const { api } = useFramework()
  return useQuery({
    queryKey: dashboardKeys.teamIndex(teamId!, loginId!, day),
    queryFn: async (): Promise<TeamIndexData> => {
      const response = await api.get_Team_Index({
        loginId: loginId!,
        teamId: teamId!,
        type: day
      })
      const firstData = response.data[0]
      return {
        topPlayers: firstData?.topPlayers ?? [],
        playersOfDay: firstData?.playersOfDay ?? []
      }
    },
    enabled: !!teamId && !!loginId && options?.enabled !== false
  })
}

export function useDashboardConfigQuery(
  loginId: LoginId | undefined,
  options?: { enabled?: boolean }
) {
  const { api } = useFramework()
  return useQuery({
    queryKey: dashboardKeys.dashboardConfig(loginId!),
    queryFn: async (): Promise<DashboardConfigData> => {
      const response = await api.get_Login_LoginMeta({
        loginId: loginId!,
        metaKey: MetaKey.DashboardConfig
      })
      return extractKeyValuePairs(response.data || defaultDashboardConfig)
    },
    enabled: !!loginId && options?.enabled !== false
  })
}

export function useRecentActivityQuery(
  teamId: TeamId | undefined,
  options?: { enabled?: boolean }
) {
  const { api } = useFramework()
  return useQuery({
    queryKey: dashboardKeys.recentActivity(teamId!),
    queryFn: async (): Promise<
      Database_Procs_GetRecentActivityByTeam_Item[]
    > => {
      const response = await api.get_Team_RecentActivity({
        teamId: teamId!,
        batchSize: 10
      })
      return response.recentActivityByTeams
    },
    enabled: !!teamId && options?.enabled !== false
  })
}

export function useFluencyGamesQuery(
  teamId: TeamId | undefined,
  year: string | undefined,
  grade: number | null | undefined,
  options?: { enabled?: boolean }
) {
  const { api } = useFramework()
  return useQuery({
    queryKey: dashboardKeys.fluencyGames(teamId!, year!, grade as number),
    queryFn: async (): Promise<
      Entities_FluencyGamesResult_TeamResult | undefined
    > => {
      const response = await api.get_Assessment_FluencyGames({
        id: teamId!,
        year: year!,
        grade: $SpecialGradeLevel.fromOptional(grade) ?? SpecialGradeLevel.Null,
        level: EntityLevel.Team
      })
      return response.fluencyGames[0]?.team
    },
    enabled: !!teamId && !!year && grade != null && options?.enabled !== false
  })
}

export function useTeamHistoryQuery(
  teamId: TeamId | undefined,
  mode: number,
  options?: { enabled?: boolean }
) {
  const { api } = useFramework()
  return useQuery({
    queryKey: dashboardKeys.teamHistory(teamId!, mode),
    queryFn: async (): Promise<
      Database_Procs_GetTeamHistory_Item | undefined
    > => {
      const response = await api.get_Team_TeamHistory({
        teamId: teamId!,
        mode
      })
      return response.teamHistory
    },
    enabled: !!teamId && options?.enabled !== false
  })
}

// --- Mutations ---

export function useSaveDashboardConfig() {
  const { api } = useFramework()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      loginId,
      config
    }: {
      loginId: LoginId
      config: DashboardConfigData
    }) => {
      await api.post_Login_UpsertLoginMeta({
        loginId,
        metaKey: MetaKey.DashboardConfig,
        data: {
          quickLinks: {
            focusMode: config.focusMode || false,
            wallOfFameTool: config.wallOfFameTool || false
          },
          sections: {
            goalsIndex: config.goalsIndex || false,
            teamActivityChart: config.teamActivityChart || false,
            playersOfTheDay: config.playersOfTheDay || false,
            topPlayers: config.topPlayers || false,
            teamActivity: config.teamActivity || false
          },
          performanceList: {
            gyms: config.gyms || false,
            skillSets: config.skillSets || false,
            knowAndShow: config.knowAndShow || false,
            veryImportantFacts: config.veryImportantFacts || false,
            justTheFacts: config.justTheFacts || false
          },
          generalSettings: {
            optOutOfBeta: config.optOutOfBeta || false
          },
          showDataOnGrid: config.showDataOnGrid || false
        }
      })
      return config
    },
    onSuccess: (config, { loginId }) => {
      queryClient.setQueryData(dashboardKeys.dashboardConfig(loginId), config)
    }
  })
}

export function useSubmitAppFeedback() {
  const { api } = useFramework()
  return useMutation({
    mutationFn: async ({
      loginId,
      reasonTitle,
      reasonDescription
    }: {
      loginId: LoginId
      reasonTitle: string
      reasonDescription: string
    }) => {
      await api.post_Team_AddAppFeedback({
        loginId,
        data: { reasonTitle, reasonDescription }
      })
    }
  })
}

// --- Helpers ---

const defaultDashboardConfig = {
  quickLinks: {
    focusMode: true,
    wallOfFameTool: true
  },
  sections: {
    goalsIndex: true,
    teamActivityChart: true,
    playersOfTheDay: true,
    topPlayers: true,
    teamActivity: true
  },
  performanceList: {
    gyms: true,
    skillSets: true,
    knowAndShow: true,
    veryImportantFacts: true,
    justTheFacts: true
  },
  generalSettings: {
    optOutOfBeta: true
  },
  showDataOnGrid: false
}

function extractKeyValuePairs(
  obj: Record<string, unknown>
): DashboardConfigData {
  const result: DashboardConfigData = {}

  function traverse(obj: Record<string, unknown>) {
    Object.keys(obj).forEach((key) => {
      const value = obj[key]
      if (typeof value === 'object' && value !== null) {
        traverse(value as Record<string, unknown>)
      } else {
        result[key] = value as boolean
      }
    })
  }

  traverse(obj)
  return result
}
